import React, { useState, useRef } from "react";
import {
  Box,
  Dialog,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachmentIcon from "@mui/icons-material/Attachment";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import { FieldLabel, StyledTextField } from "../types/company-form";

interface AddDocumentDrawerProps {
  open: boolean;
  onClose: () => void;
  companyId: number;
  onSuccess: () => void;
}

export default function AddDocumentDrawer({
  open,
  onClose,
  companyId,
  onSuccess,
}: AddDocumentDrawerProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color100 = reduxTheme.primary_colors?.[100];
  const color300 = reduxTheme.primary_colors?.[300];
  const color400 = reduxTheme.primary_colors?.[400] ?? "#8B4513";
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color900 = reduxTheme.primary_colors?.[900];

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const handleClose = () => {
    setName("");
    setDescription("");
    setFile(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Please select a file to upload.");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("company", String(companyId));
      formData.append("document", file);
      if (name.trim()) formData.append("name", name.trim());
      if (description.trim())
        formData.append("description", description.trim());

      await apiService.post("api/companies/company-documents/", formData);
      toast.success("Document uploaded successfully!");
      onSuccess();
      handleClose();
    } catch (err) {
      console.error("Failed to upload document:", err);
      toast.error("Failed to upload document. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          bgcolor: color900,
          borderRadius: "20px",
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: `linear-gradient(135deg, ${color400} 0%, #c9834a 100%)`,
          borderRadius: "20px 20px 0 0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AttachmentIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ color: "#fff" }}
          >
            Upload Document
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Form */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          py: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Box>
          <FieldLabel textcolor={color100}>Document Name</FieldLabel>
          <StyledTextField
            placeholder="e.g. Service Contract"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>

        <Box>
          <FieldLabel textcolor={color100}>Description</FieldLabel>
          <StyledTextField
            placeholder="Additional details about this document..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={3}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />
        </Box>

        <Box>
          <FieldLabel textcolor={color100}>
            File <span className="required">*</span>
          </FieldLabel>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleFileChange}
          />
          <Button
            variant="outlined"
            fullWidth
            startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontSize: 13,
              py: 1.5,
              borderColor: color400,
              color: color400,
              justifyContent: "flex-start",
            }}
          >
            {file ? file.name : "Choose file"}
          </Button>
          {file && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </Typography>
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: color900,
          borderRadius: "0 0 20px 20px",
        }}
      >
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !file}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 4,
            bgcolor: color400,
            "&:hover": { bgcolor: color400 },
          }}
        >
          {submitting ? "Uploading..." : "Upload Document"}
        </Button>
      </Box>
    </Dialog>
  );
}
